/* ---- Explore: sticky filter bar ----
 * One continuous rule decides where the bar sits, on every viewport:
 *
 *     bar top = max(resting place in the flow, stick line)
 *
 * The stick line is the header's bottom edge while Headroom shows the header
 * and the top of the viewport while it is hidden; a change is tweened in step
 * with the header's own slide transition. Because the rule is continuous in
 * the scroll position there is no moment of "switching to sticky" – the bar
 * arrives at the line and leaves it again without a jump.
 *
 * The bar (dock) is lifted out of the page content, fixed, and positioned
 * every frame; the slot keeps its place in the flow. That is needed twice over:
 *   - with ScrollSmoother (mouse) the content is moved with a transform, which
 *     breaks position:sticky/fixed inside it. The bar follows the smoother's
 *     rendered offset, so it travels exactly with the eased content.
 *   - with native scrolling (touch) position:sticky is no option either:
 *     html and body both set overflow-x:hidden, which makes body the sticky
 *     scrollport and nothing ever sticks. Here the bar follows window.scrollY.
 *
 * --stuck (0 → 1 over the last STUCK_RANGE px before the line) lets the CSS
 * ease the bar into its compact, shadowed look and back.
 * Markup: kkg-templates/content-explore.php, styles: assets/css/explore.css.
 */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const STUCK_RANGE = 80;
/* Match $header-slide-duration / $header-slide-ease in _header.scss
   (cubic-bezier(0.65, 0, 0.35, 1) is GSAP's cubic.inOut) */
const LINE_DURATION = 0.6;
const LINE_EASE = 'cubic.inOut';
const AUTO_CLOSE_DISTANCE = 60;

let instance = null;

export function initExploreStickyFilter() {
  destroyExploreStickyFilter();

  /* A dock left behind outside the swapped SWUP container by an earlier visit */
  document.querySelectorAll('[data-explore-filterbar-dock].is-floating').forEach((el) => el.remove());

  const slot = document.querySelector('[data-explore-filterbar]');
  const dock = slot && slot.querySelector('[data-explore-filterbar-dock]');
  if (!slot || !dock) return;

  const root = document.documentElement;
  const smoother = window._scrollSmoother || null;
  const content = smoother ? smoother.content() : null;
  const panel = dock.querySelector('#explore-project-filter');
  const toggle = dock.querySelector('.btn-filter');
  const $ = window.jQuery;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const line = { value: 0 };
  let slotDocTop = 0;
  let lastStuck = -1;
  let panelOpenedAt = null;
  let lineTarget = 0;

  const setDockY = gsap.quickSetter(dock, 'y', 'px');

  const getHeaderHeight = () =>
    parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 0;

  const getAdminBarHeight = () => {
    const bar = document.getElementById('wpadminbar');
    return bar && getComputedStyle(bar).position === 'fixed' ? bar.offsetHeight : 0;
  };

  const getLineTarget = () =>
    getAdminBarHeight() + (root.classList.contains('headroom--unpinned') ? 0 : getHeaderHeight());

  /* How far the page content is currently shifted up (≤ 0) */
  const getContentY = () => (content ? Number(gsap.getProperty(content, 'y')) || 0 : -window.scrollY);

  function measure() {
    moveLine();
    /* Resting height: measured uncompacted, then held by the slot so the page
       below never moves while the bar compacts. */
    dock.style.setProperty('--stuck', '0');
    slot.style.setProperty('--explore-filterbar-height', dock.offsetHeight + 'px');
    lastStuck = -1;

    slotDocTop = slot.getBoundingClientRect().top - getContentY();
    update();
  }

  function update() {
    /* Where the bar would be if it simply scrolled with the page */
    const restTop = slotDocTop + getContentY();

    setDockY(Math.max(restTop, line.value));

    /* Ease in over the last stretch before the line – or over the whole way
       there if the bar rests closer to the line than that (mobile), so it is
       never "half stuck" at the top of the page. */
    const range = gsap.utils.clamp(1, STUCK_RANGE, slotDocTop - getAdminBarHeight() - getHeaderHeight());
    const stuck = Math.round(gsap.utils.clamp(0, 1, 1 - (restTop - line.value) / range) * 100) / 100;
    if (stuck !== lastStuck) {
      lastStuck = stuck;
      dock.style.setProperty('--stuck', String(stuck));
      dock.classList.toggle('is-stuck', stuck === 1);
    }

    /* An open filter list closes once the reader scrolls on */
    if (panelOpenedAt !== null && Math.abs(window.scrollY - panelOpenedAt) > AUTO_CLOSE_DISTANCE) {
      closePanel();
    }
  }

  function moveLine() {
    const target = getLineTarget();
    if (target === lineTarget) return;
    lineTarget = target;
    gsap.killTweensOf(line);
    if (reducedMotion) {
      line.value = target;
      return;
    }
    gsap.to(line, { value: target, duration: LINE_DURATION, ease: LINE_EASE });
  }

  function closePanel(returnFocus) {
    panelOpenedAt = null;
    if (!panel || !$ || !panel.classList.contains('show')) return;
    $(panel).collapse('hide');
    if (returnFocus && toggle) toggle.focus();
  }

  function onPanelShown() {
    panelOpenedAt = window.scrollY;
  }

  function onPanelHidden() {
    panelOpenedAt = null;
  }

  function onDockClick(e) {
    /* Picking a filter in the list is done – close it */
    if (panel && e.target.closest('.explore-filter-button') && panel.contains(e.target)) {
      closePanel();
    }
  }

  function onDockKeydown(e) {
    if (e.key === 'Escape' && panel && panel.classList.contains('show')) {
      closePanel(true);
    }
  }

  /* Out of the page content, right before it: keeps the tab order
     header → filters → content. */
  const wrapper = document.getElementById('smooth-wrapper');
  if (!wrapper) return;
  wrapper.parentNode.insertBefore(dock, wrapper);
  dock.classList.add('is-floating');

  lineTarget = getLineTarget();
  line.value = lineTarget;

  /* Headroom state lives in <html>'s classes, --header-height in its style */
  const headroomObserver = new MutationObserver(moveLine);
  headroomObserver.observe(root, { attributes: true, attributeFilter: ['class', 'style'] });

  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
  if (resizeObserver) resizeObserver.observe(dock);

  window.addEventListener('resize', measure);
  ScrollTrigger.addEventListener('refresh', measure);
  dock.addEventListener('click', onDockClick);
  dock.addEventListener('keydown', onDockKeydown);
  if (panel && $) {
    $(panel).on('shown.bs.collapse.exploreSticky', onPanelShown);
    $(panel).on('hidden.bs.collapse.exploreSticky', onPanelHidden);
  }

  gsap.ticker.add(update);
  measure();

  instance = {
    destroy() {
      gsap.ticker.remove(update);
      gsap.killTweensOf(line);
      headroomObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      ScrollTrigger.removeEventListener('refresh', measure);
      dock.removeEventListener('click', onDockClick);
      dock.removeEventListener('keydown', onDockKeydown);
      if (panel && $) $(panel).off('.exploreSticky');
      /* The dock lives outside the SWUP container – take it along */
      dock.remove();
    },
  };
}

export function destroyExploreStickyFilter() {
  if (!instance) return;
  instance.destroy();
  instance = null;
}
